import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import axios from 'axios';
import { auth } from '../firebaseConfig';
import styles from './ArtistLanding.module.css';
import { trackCompleteRegistration } from '../analytics/metaPixel';

const HERO_RIGHT_IMAGE = 'https://cdn.shopify.com/s/files/1/0438/8087/4152/files/artist-selection.png?v=1738970493';

const ArtistLanding = () => {
  const navigate = useNavigate();

  // Inline signup form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'artist'
  });
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handler = (event) => console.error('UNHANDLED PROMISE REJECTION:', event.reason);
    window.addEventListener('unhandledrejection', handler);
    return () => window.removeEventListener('unhandledrejection', handler);
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);

      // Fire CompleteRegistration as soon as Firebase account creation succeeds
      try {
        trackCompleteRegistration({
          status: 'success',
          content_name: 'Artist Signup',
          content_category: 'artist_onboarding',
          value: 0,
          currency: 'USD'
        });
      } catch (_) {}

      const token = await userCredential.user.getIdToken();

      try {
        await sendEmailVerification(userCredential.user);
      } catch (verificationError) {
        console.warn('Email verification failed (non-blocking):', verificationError);
      }

      const baseURL = import.meta.env.PROD
        ? 'https://art-emerge-4744deb5e11a.herokuapp.com/api'
        : 'http://localhost:5051/api';

      await axios.post(
        `${baseURL}/users/initialize`,
        {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          firebase_uid: userCredential.user.uid
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Create/Sync Shopify customer and opt into marketing (handled server-side)
      try {
        await axios.post(
          `${baseURL}/users/sync`,
          { role: formData.role },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            }
          }
        );
      } catch (syncErr) {
        // Don't block successful signup if Shopify sync fails
        console.warn('⚠️ Shopify sync failed (non-blocking):', syncErr?.response?.data || syncErr.message);
      }

      // Best DX: navigate straight to dashboard; backend dev bypass covers local
      setMessage('🎉 Account created! Redirecting...');
      setTimeout(() => {
        setIsLoading(false);
        navigate('/dashboard');
      }, 500);
    } catch (error) {
      console.error('Signup error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setMessage('Email already in use. Please log in.');
        setTimeout(() => navigate('/login'), 1200);
      } else {
        const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message;
        setMessage(`Failed to create account: ${errorMessage}`);
      }
      setIsLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('ae-signup-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={styles.page}>
      {/* Hero - Two Column */}
      <section className={styles.heroTwoCol}>
        <div className={styles.containerTwoCol}>
          <div className={styles.heroLeft}>
            <div className={styles.heroPanel}> 
              <h1 className={styles.heroTitle}>Ship Art Smarter. Sell More. Keep More.</h1>
              <p className={styles.heroSubtitle}>
                Join Art Emerge and get up to 75% off shipping labels for any artwork — domestic or international.
              </p>
              <ul className={styles.heroBullets}>
                <li>✓ You set the artwork price — we show buyer fees and estimated payout clearly</li>
                <li>✓ No listing fees — publish your art for free</li>
                <li>✓ Estimated shipping is built into the buyer-facing price</li>
                <li>✓ AI-powered promotion — get discovered by collectors & galleries</li>
                <li>✓ Professional portfolio tools — stand out from the crowd</li>
              </ul>
              <div className={styles.heroCtas}>
                <button className={styles.ctaPrimary} onClick={scrollToForm}>Create your account</button>
              </div>
            </div>
          </div>
          <div className={styles.heroRight}>
            <img className={styles.heroImg} src={HERO_RIGHT_IMAGE} alt="Featured artist selection" />
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className={styles.valueSection}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Why join Art Emerge as an Artist?</h2>
          {/* Auto-scrolling single-row marquee */}
          <div className={styles.marqueeViewport} aria-label="Key benefits" role="region">
            <div className={styles.marqueeTrack}>
              {[1,2].map((dup) => (
                <>
                  <div className={styles.valueCard} key={`earn-${dup}`}>
                    <h3>Keep More of What You Earn</h3>
                    <ul>
                      <li>15% seller fee on your artwork price, shown before you publish</li>
                      <li>Estimated take-home is calculated in your upload dashboard</li>
                      <li>No listing fees — publish your art for free</li>
                    </ul>
                  </div>
                  <div className={styles.valueCard} key={`discover-${dup}`}>
                    <h3>Get Discovered Without the Hassle</h3>
                    <ul>
                      <li>AI-powered promotion to reach collectors, galleries, and art lovers worldwide</li>
                      <li>Smart search shows your work to the right buyers</li>
                    </ul>
                  </div>
                  <div className={styles.valueCard} key={`sell-${dup}`}>
                    <h3>Streamlined Selling Experience</h3>
                    <ul>
                      <li>Estimated collector shipping is added to the buyer-facing price</li>
                      <li>Heavily discounted labels — up to 75% off standard rates</li>
                      <li>Domestic and international shipping tools built in</li>
                      <li>Simple setup — no coding or complex store management</li>
                    </ul>
                  </div>
                  <div className={styles.valueCard} key={`reputation-${dup}`}>
                    <h3>Grow Your Reputation & Network</h3>
                    <ul>
                      <li>Showcase a professional, polished portfolio</li>
                      <li>Connect with galleries, curators, and artists</li>
                    </ul>
                  </div>
                  <div className={styles.valueCard} key={`control-${dup}`}>
                    <h3>You’re in Control</h3>
                    <ul>
                      <li>Set your own prices and manage inventory with ease</li>
                      <li>Flexible fulfillment — you control availability and timelines</li>
                    </ul>
                  </div>
                </>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className={styles.feeSection}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>How pricing and fees work</h2>
          <div className={styles.feeGrid}>
            <div className={styles.feeCard}>
              <h3>1. You set your artwork price</h3>
              <p>This is the amount you are charging for the artwork itself. Your dashboard keeps this number separate from buyer fees and shipping estimates.</p>
            </div>
            <div className={styles.feeCard}>
              <h3>2. We estimate collector shipping</h3>
              <p>Based on the artwork dimensions, Art Emerge adds a conservative estimated shipping amount to the buyer-facing price. After the sale, you still use the shipping-label tool to purchase the actual label once the work is packaged.</p>
            </div>
            <div className={styles.feeCard}>
              <h3>3. Buyer platform fee is added</h3>
              <p>A 10% buyer platform/referral fee is added to the artwork price plus estimated shipping, so the storefront price matches what collectors see before checkout.</p>
            </div>
            <div className={styles.feeCard}>
              <h3>4. Your estimated payout is shown</h3>
              <p>Art Emerge deducts a 15% seller fee from your artwork price. Your upload dashboard shows the estimated payout before you publish.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Inline Signup */}
      <section id="ae-signup-form" className={styles.signupSection}>
        <div className={styles.containerGrid}>
          <div className={styles.signupCopy}>
            <h2>Get started free — publish your first artwork today</h2>
            <p>No listing fees. Clear buyer pricing, estimated shipping, and estimated artist payout before you publish.</p>
            <div className={styles.trustBullets}>
              <span>✓ Up to 75% off labels</span>
              <span>✓ 15% seller fee shown clearly</span>
              <span>✓ 10% buyer fee included in storefront price</span>
              <span>✓ AI promotion</span>
            </div>
          </div>
          <div className={styles.signupCard}>
            <form onSubmit={handleSignup} className={styles.form}>
              {message && (
                <div className={`${styles.alert} ${message.includes('🎉') ? styles.success : styles.error}`}>{message}</div>
              )}
              <div className={styles.formRow}>
                <input
                  className={styles.input}
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                />
              </div>
              <div className={styles.formRow}>
                <input
                  className={styles.input}
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                />
              </div>
              <div className={styles.formRow}>
                <input
                  className={styles.input}
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                />
              </div>
              <div className={styles.formRowInline}>
                <label className={styles.label}>Role</label>
                <select
                  className={styles.select}
                  name="role"
                  onChange={handleChange}
                  value={formData.role}
                  disabled={isLoading}
                  required
                >
                  <option value="artist">Artist</option>
                  <option value="gallery">Gallery</option>
                  <option value="curator">Curator</option>
                  <option value="collector">Collector</option>
                </select>
              </div>
              <button type="submit" className={styles.submit} disabled={isLoading}>
                {isLoading ? 'Creating account...' : 'Create your account'}
              </button>
              <div className={styles.disclaimer}>
                By creating an account you agree to our
                {' '}<a href="https://artemerge.org/policies/terms-of-service" target="_blank" rel="noopener noreferrer">Terms</a>
                {' '}and{' '}
                <a href="https://artemerge.org/policies/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Bottom CTA intentionally removed (form above handles conversion) */}
    </div>
  );
};

export default ArtistLanding;


